import React, { useContext, useEffect, useState } from "react";
import { getServices } from "services/api/connection";
import LoaderBlock from "shared/loader/LoaderBlock";
import {
  addKitchenMarketPlaceMarkup,
  getKitchenMarketplaceMarkupList,
} from "services/api/kitchen";
import UserContext from "store/user-context";
import toastify from "shared/toastr";
import { RoleType } from "constants/roleType";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, useFieldArray } from "react-hook-form";
import { numberFormat } from "constants/numberFormat";

const MarketplaceMarkupAdjustment = (props) => {
  const [isLoading, setisLoading] = useState(false);
  const userCtx = useContext(UserContext);
  const userRole = userCtx.userRole;
  const isEdit =  userRole === RoleType.ADMIN || userRole === RoleType.ADMIN_STAFF;

  const validationSchema = Yup.object().shape({
    marketplaces: Yup.array().of(
      Yup.object({
        markupValue: Yup.number()
          .typeError("Only digits allowed")
          .positive("Markup value must be a positive number")
          .min(0, `Markup cannot be negative`)
          .max(1000, ({ max }) => `Markup not more than ${max}`)
          .test(
            "validDollarType",
            "Markup can have upto 2 digits after decimal",
            (value) => /^\d*.?\d{1,2}$/.test(value)
          ),
      })
    ),
  });

  const methods = useForm({
    mode: "all",
    resolver: yupResolver(validationSchema),
    defaultValues: {
      markupType: "Percentage",
      markupValue: 0,
      isEnabled: true,
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isValid },
  } = methods;

  const { fields } = useFieldArray({
    control,
    name: "marketplaces",
    keyName: "mktId",
  });

  useEffect(() => {
    getDeliveryServices();
    getMarketplaceMarkupList();
  }, []);

  const getDeliveryServices = async () => {
    setisLoading(true);
    await getServices()
      .then((res) => {
        const data = res.data;
        const marketplaces = data.map((item) => ({
          name: item.name,
          marketplaceId: item.id,
          markupType: "Percentage",
          markupValue: 0,
          isEnabled: true,
        }));
        reset({
          marketplaces: marketplaces
        });
        setisLoading(false);
      })
      .catch(() => {
        setisLoading(false);
      });
  };

  const getMarketplaceMarkupList = async () => {
    setisLoading(true);
    await getKitchenMarketplaceMarkupList(props?.kitchen?.id, props?.type)
      .then((res) => {
        const data = res.data;
        if (data && data?.length) {
          reset({
            marketplaces: data,
          });
        }
        setisLoading(false);
      })
      .catch(() => {
        setisLoading(false);
      });
  };

  const onSubmit = async (data) => {
    const model = {
      kitchenId: props?.kitchen?.id,
      kitchenName: props?.kitchen?.name,
      userId: userCtx?.userInfo?.userId,
      options: data?.marketplaces,
    };
    await addKitchenMarketPlaceMarkup(model)
      .then((res) => {
        toastify("success", res.message);
        getMarketplaceMarkupList();
      })
      .catch((e) => {
        toastify("error", e.message);
      });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="details__wrapper">
        {isLoading ? (
          <LoaderBlock />
        ) : (
          <div className="details__wrapper__inner">
            <div className="order__filters">
              <div className="channel__wrapper">
                <div className="dropdown">
                  <div className="dropdown__action__wrapper p-0 card__no__border">
                    {fields && fields.length ? (
                      <div className="sortBy__marketPlaces">
                        <div className="detailCard mb-3 pb-0">
                          <h5>Marketplace Markup Configuration</h5>
                        </div>
                        <div className="row">
                          {fields.map((option, idx) => (
                            <div
                              className="col-md-12 marketplace__markup"
                              key={idx}
                            >
                              <label className="col-12 col-md-2 custom-checkbox-container mb-0">
                                {option.name}
                              </label>
                              <div className="col-12 col-md-3" key={idx}>
                                <select
                                  className="form-control mb-0"
                                  {...register(
                                    `marketplaces.${idx}.markupType`
                                  )}
                                  disabled={!isEdit}
                                >
                                  <option value="Percentage">Percentage</option>
                                  <option value="Fixed">Fixed</option>
                                </select>
                              </div>

                              <div className="col-12 col-md-3">
                                <input
                                  key={option.mktId}
                                  type="string"
                                  placeholder="e.g. 15.00"
                                  className={`form-control mb-0 ${
                                    errors?.marketplaces?.[idx]?.markupValue
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  {...register(
                                    `marketplaces.${idx}.markupValue`
                                  )}
                                  disabled={!isEdit}
                                />
                                {errors?.marketplaces?.[idx]?.markupValue
                                  ?.message && (
                                  <div
                                    className="invalid-feedback d-block"
                                    style={{
                                      position: "absolute",
                                      margin: "0px",
                                    }}
                                  >
                                    {
                                      errors?.marketplaces?.[idx]?.markupValue
                                        ?.message
                                    }
                                  </div>
                                )}
                              </div>

                              <div
                                className="col-12 col-md-2 switches right__switch"
                                key={option.mktId}
                              >
                                <label className="switch">
                                  <input
                                    type="checkbox"
                                    {...register(
                                      `marketplaces.${idx}.isEnabled`
                                    )}
                                    disabled={!isEdit}
                                  />
                                  <span className="slider round"></span>
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {isEdit && (
                      <div className="button__wrapper">
                        <button
                          className="btn apply-btn"
                          disabled={isLoading || !isValid}
                        >
                          Apply
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </form>
  );
};

export default MarketplaceMarkupAdjustment;
